import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Snackbar, Alert } from '@mui/material';

import CustomButton from '../../../common/CustomButton';
import CustomInput from '../../../common/CustomInput';

import {
  useGetIdeasQuery,
  useDeleteIdeaMutation,
  useSaveFlightToIdeaMutation,
  useSavePlaceToIdeaMutation,
  useSaveTourToIdeaMutation,
} from '../../../store/services/ideasApi';
import { useIsMobile } from '../../../hooks/useWindowWidth';
import {
  PENDING_FLIGHT_TO_IDEA_STORAGE_KEY,
  PENDING_PLACE_TO_IDEA_STORAGE_KEY,
  PENDING_TOUR_TO_IDEA_STORAGE_KEY,
} from '../../../constants/discoverIdeaFlow';

import styles from './MyIdeas.module.scss';
import { FilterIcon, SearchIcon } from '../../../components/Icons';
import { IdeasFilters, IdeasQueryParams, Idea } from '../../../types/idea.types';
import type { FlightToIdeaPayload } from '../../../types/flight.types';
import type { PlaceToIdeaPayload } from '../../../types/place.types';
import type { TourToIdeaPayload } from '../../../types/tour.types';
import CreateIdeaModal from './components/CreateIdeaModal/CreateIdeaModal';
import ConfirmDeleteModal from '../../../common/ConfirmDeleteModal/ConfirmDeleteModal';
import IdeasFiltersDrawer from './components/IdeasFiltersDrawer/IdeasFiltersDrawer';
import IdeaCard from './components/IdeaCard/IdeaCard';

const MyIdeas: FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null);
  const [fromDiscoverFlow, setFromDiscoverFlow] = useState(false);
  const discoverOpenerRef = useRef(false);
  const [ideaToastOpen, setIdeaToastOpen] = useState(false);
  const [ideaToastMessage, setIdeaToastMessage] = useState('');
  const [ideaDeleteConfirmOpen, setIdeaDeleteConfirmOpen] = useState(false);
  const [ideaPendingDelete, setIdeaPendingDelete] = useState<Idea | null>(null);
  const [isDeletingIdea, setIsDeletingIdea] = useState(false);

  const [filters, setFilters] = useState<IdeasFilters>({
    sortBy: 'edited_at',
    sortOrder: 'desc',
  });

  const queryParams = useMemo((): IdeasQueryParams => {
    const params: IdeasQueryParams = {};

    const orderPrefix = filters.sortOrder === 'desc' ? '-' : '';
    params.ordering = `${orderPrefix}${filters.sortBy}`;

    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    return params;
  }, [filters, searchQuery]);

  const { data: ideas, isLoading, refetch } = useGetIdeasQuery(queryParams);
  const [deleteIdea] = useDeleteIdeaMutation();
  const [saveFlightToIdea] = useSaveFlightToIdeaMutation();
  const [savePlaceToIdea] = useSavePlaceToIdeaMutation();
  const [saveTourToIdea] = useSaveTourToIdeaMutation();

  useEffect(() => {
    if (searchParams.get('fromDiscover') !== '1') {
      discoverOpenerRef.current = false;
      return;
    }
    if (discoverOpenerRef.current) return;
    discoverOpenerRef.current = true;

    const stripQuery = () => {
      navigate('/ideas', { replace: true });
    };

    try {
      const flightRaw = sessionStorage.getItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY);
      const placeRaw = sessionStorage.getItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY);
      const tourRaw = sessionStorage.getItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY);
      const raw = flightRaw ?? placeRaw ?? tourRaw;
      if (!raw) {
        stripQuery();
        return;
      }
      JSON.parse(raw) as { body: FlightToIdeaPayload | PlaceToIdeaPayload | TourToIdeaPayload };
      setFromDiscoverFlow(true);
      setOpenModal(true);
    } catch {
      try {
        sessionStorage.removeItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    stripQuery();
  }, [searchParams, navigate]);

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    setEditingIdea(null);
    if (fromDiscoverFlow) {
      setFromDiscoverFlow(false);
      try {
        sessionStorage.removeItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  };

  const toggleFilters = (newOpen: boolean) => {
    setOpenFilters(newOpen);
  };

  const handleIdeaCreated = async (created?: Idea, editedName?: string) => {
    if (created) {
      setIdeaToastMessage(`Idea '${created.name}' was created successfully.`);
      setIdeaToastOpen(true);
    } else if (editedName?.trim()) {
      setIdeaToastMessage(`Idea '${editedName.trim()}' was updated successfully.`);
      setIdeaToastOpen(true);
    }

    let pendingFlightBody: FlightToIdeaPayload | undefined;
    let pendingPlaceBody: PlaceToIdeaPayload | undefined;
    let pendingTourBody: TourToIdeaPayload | undefined;
    if (fromDiscoverFlow && created?.id) {
      try {
        const flightRaw = sessionStorage.getItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY);
        if (flightRaw) {
          pendingFlightBody = (JSON.parse(flightRaw) as { body?: FlightToIdeaPayload }).body;
        }
        const placeRaw = sessionStorage.getItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY);
        if (placeRaw) {
          pendingPlaceBody = (JSON.parse(placeRaw) as { body?: PlaceToIdeaPayload }).body;
        }
        const tourRaw = sessionStorage.getItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY);
        if (tourRaw) {
          pendingTourBody = (JSON.parse(tourRaw) as { body?: TourToIdeaPayload }).body;
        }
      } catch {
        /* ignore */
      }
    }

    await refetch();

    if (!created?.id) {
      return;
    }

    if (pendingFlightBody) {
      setFromDiscoverFlow(false);
      try {
        await saveFlightToIdea({ ideaId: created.id, body: pendingFlightBody }).unwrap();
      } catch (e) {
        console.error('Failed to attach Discover flight to new idea:', e);
      } finally {
        try {
          sessionStorage.removeItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
      await refetch();
    } else if (pendingPlaceBody) {
      setFromDiscoverFlow(false);
      try {
        await savePlaceToIdea({ ideaId: created.id, body: pendingPlaceBody }).unwrap();
      } catch (e) {
        console.error('Failed to attach Discover place to new idea:', e);
      } finally {
        try {
          sessionStorage.removeItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
      await refetch();
    } else if (pendingTourBody) {
      setFromDiscoverFlow(false);
      try {
        await saveTourToIdea({ ideaId: created.id, body: pendingTourBody }).unwrap();
      } catch (e) {
        console.error('Failed to attach Discover tour to new idea:', e);
      } finally {
        try {
          sessionStorage.removeItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
      await refetch();
    } else if (fromDiscoverFlow) {
      setFromDiscoverFlow(false);
      try {
        sessionStorage.removeItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_PLACE_TO_IDEA_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  };

  const handleDeleteIdeaRequest = (idea: Idea) => {
    setIdeaPendingDelete(idea);
    setIdeaDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteIdea = async () => {
    if (!ideaPendingDelete) return;
    setIsDeletingIdea(true);
    try {
      await deleteIdea(ideaPendingDelete.id.toString()).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to delete idea:', error);
    } finally {
      setIsDeletingIdea(false);
      setIdeaDeleteConfirmOpen(false);
      setIdeaPendingDelete(null);
    }
  };

  const handleEditIdea = (idea: Idea) => {
    setEditingIdea(idea);
    setOpenModal(true);
  };

  const handleFiltersChange = (newFilters: IdeasFilters) => {
    setFilters(newFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <>
      <CreateIdeaModal
        open={openModal}
        onClose={handleModalClose}
        onSuccess={handleIdeaCreated}
        isEditMode={!!editingIdea}
        ideaData={editingIdea || undefined}
      />

      <Snackbar
        open={ideaToastOpen}
        autoHideDuration={6000}
        onClose={() => setIdeaToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setIdeaToastOpen(false)}
          severity="success"
          sx={{ width: '100%', maxWidth: 480 }}
        >
          {ideaToastMessage}
        </Alert>
      </Snackbar>

      <ConfirmDeleteModal
        open={ideaDeleteConfirmOpen}
        onClose={() => !isDeletingIdea && setIdeaDeleteConfirmOpen(false)}
        message={ideaPendingDelete ? `You want to remove the idea “${ideaPendingDelete.name}”?` : ''}
        confirmLabel="Delete idea"
        onConfirm={handleConfirmDeleteIdea}
        isConfirming={isDeletingIdea}
      />

      <IdeasFiltersDrawer
        openFilters={openFilters}
        toggleFilters={toggleFilters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <Box className={styles.myIdeas}>
        <Box className={styles.ideasHeader}>
          <Box className={styles.ideasHeaderLeft}>
            <Typography
              variant={isMobile ? 'h6Semibold' : 'h4Bold'}
              color="#222B3A"
            >
              My Ideas
            </Typography>
            <Typography
              variant={isMobile ? 'h11' : 'h10'}
              sx={{ color: '#5E6A81' }}
            >
              List of your ideas for future travels
            </Typography>
          </Box>

          <CustomButton
            variant="contained"
            onClick={handleModalOpen}
            customClass={styles.customButton}
          >
            {isMobile ? <Typography variant="h3">+</Typography> : '+ Create new'}
          </CustomButton>
        </Box>

        <Box className={styles.filtersWrapper}>
          <Box className={styles.searchWrapper}>
            <CustomInput
              inputType="text"
              inputName="search"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              startIcon={<SearchIcon size={20} />}
              endIcon={
                searchQuery ? (
                  <Box
                    onClick={clearSearch}
                    sx={{ cursor: 'pointer', color: '#5e6a81' }}
                  >
                    ×
                  </Box>
                ) : undefined
              }
            />
          </Box>

          <Box
            className={styles.filterIconWrapper}
            onClick={() => toggleFilters(true)}
          >
            <FilterIcon size={20} />
            {!isMobile && (
              <Typography
                sx={{ color: '#5E6A81' }}
                variant="h10Semibold"
              >
                Filters
              </Typography>
            )}
          </Box>
        </Box>

        <Box className={styles.contentWrapper}>
          <Box className={styles.ideasListContainer}>
            {isLoading && <Typography>Loading ideas...</Typography>}

            {ideas && ideas.length > 0 ? (
              <Box className={styles.ideasList}>
                {ideas.map(idea => (
                  <IdeaCard
                    key={idea.id}
                    idea={idea}
                    onDeleteRequest={handleDeleteIdeaRequest}
                    onEdit={handleEditIdea}
                    onRefetchIdeas={refetch}
                  />
                ))}
              </Box>
            ) : (
              <Box className={styles.emptyState}>
                {searchQuery ? (
                  <>
                    <Typography variant="h7">No ideas found</Typography>
                    <Typography
                      variant="h11"
                      sx={{ color: '#5E6A81' }}
                    >
                      Try adjusting your search or create a new idea
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h7">No ideas yet</Typography>
                    <Typography
                      variant="h11"
                      sx={{ color: '#5E6A81' }}
                    >
                      Create your first travel idea!
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MyIdeas;
